import {
    type ActionDescription,
    ActionKind,
    type ActionOutputDescriptor,
    type PropertyDescription,
    PropertyKind,
} from "@glide/app-description";
import type {
    WireActionResult,
    WireActionResultBuilder,
    WireActionHydrator,
    WireActionInflationBackend,
} from "@glide/wire";
import { type ActionDescriptor, ActionDisabledReason, ActionGroup } from "./action-descriptor";
import {
    ColumnPropertyFlag,
    ColumnPropertyHandler,
    EnumPropertyHandler,
    getPrimitiveNonHiddenColumnsSpec,
    makeNumberPropertyDescriptor,
    PropertySection,
    RequiredKind,
    type ActionAvailability,
    type AppDescriptionContext,
    type PropertyDescriptor,
} from "@glide/function-utils";
import { actionAvailabilityApps } from "./action-handler";
import { BaseActionHandler } from "./base";
import { ICON_PALE } from "../plugins/icon-colors";
import type { GlideIconProps, PluginTierList } from "@glide/plugins-codecs";
import { makePrimitiveType } from "@glide/type-schema";
import { Result, type BillablesConsumed } from "@glide/plugins";
import { checkString, getResponseErrorMessage, isResponseOK } from "@glide/support";
import type {
    PrintScreenToPDFOrientation,
    PrintScreenToPDFRequestBody,
} from "@glide/common-core/dist/js/firebase-function-types";
import {
    defaultOrientation,
    defaultZoomPercentage,
    maxZoomPercentage,
    minZoomPercentage,
} from "@glide/common-core/dist/js/print-screen-to-pdf";
import { isLoadingValue } from "@glide/computation-model-types";
import { isExperimentEnabled } from "@glide/common-core/dist/js/is-experiment-enabled";
import { getFeatureSetting } from "@glide/common-core/dist/js/feature-settings";
import type { StaticActionContext } from "../static-context";
import { inflateNumberProperty } from "../wire/utils";

const fileURLResultName = "fileURL";
const advancedSection = { name: "Advanced", order: 0, collapsed: true };

interface PrintScreenToPDFActionDescription extends ActionDescription {
    readonly fileURLOutputColumn: PropertyDescription;
    readonly orientation?: PropertyDescription;
    readonly zoomPercentage?: PropertyDescription;
}

const fileURLOutputPropertyHandler = new ColumnPropertyHandler(
    "fileURLOutputColumn",
    "Temporary file URL",
    [ColumnPropertyFlag.Editable, ColumnPropertyFlag.EditedInApp, ColumnPropertyFlag.AllowUserProfileColumns],
    undefined,
    ["pdf", "file", "url", "fileURL"],
    getPrimitiveNonHiddenColumnsSpec,
    "uri",
    PropertySection.Data
);

const warningPropertyDescriptor: PropertyDescriptor = {
    kind: PropertyKind.Warning,
    property: { name: "dummy" },
    label: "Watch out!",
    text: 'The generated URL is valid for only one hour. If you need the PDF for longer than that, save it via the "Upload File" action.',
    section: PropertySection.Data,
};

const orientationPropertyHandler = new EnumPropertyHandler<
    PrintScreenToPDFOrientation,
    { orientation: PrintScreenToPDFOrientation }
>(
    { orientation: defaultOrientation },
    "Orientation",
    "Orientation",
    [
        { label: "Portrait", value: "portrait" },
        { label: "Landscape", value: "landscape" },
    ],
    advancedSection,
    "dropdown"
);

const zoomPercentagePropertyDescriptor = makeNumberPropertyDescriptor(
    "zoomPercentage",
    "Zoom percentage",
    "100",
    RequiredKind.NotRequiredDefaultPresent,
    defaultZoomPercentage,
    {
        preferredType: "number",
        searchable: false,
        applyFormat: false,
        emptyByDefault: true,
        propertySection: advancedSection,
    },
    undefined,
    minZoomPercentage
);

export class PrintScreenToPDFHandler extends BaseActionHandler<PrintScreenToPDFActionDescription> {
    public readonly kind = ActionKind.PrintScreenToPDF;
    public readonly iconName: GlideIconProps = {
        icon: "st-clipboard",
        kind: "stroke",
        strokeFgColor: ICON_PALE,
    };

    public readonly name: string = "Print Screen to PDF";

    public get availability(): ActionAvailability {
        return actionAvailabilityApps;
    }

    public getTier(adc: AppDescriptionContext): PluginTierList | undefined {
        if (adc.eminenceFlags.printScreenToPDF ?? false) {
            return undefined;
        } else {
            return ["contact-sales"];
        }
    }

    public getBillablesConsumed(): BillablesConsumed | undefined {
        return 1;
    }

    public getDescriptor(
        _desc: PrintScreenToPDFActionDescription | undefined,
        { context: ccc }: StaticActionContext<AppDescriptionContext>
    ): ActionDescriptor {
        const outputs: ActionOutputDescriptor[] = [
            {
                name: fileURLResultName,
                type: makePrimitiveType("uri"),
                description: "URL pointing to the generated PDF file.",
            },
        ];

        const tier = this.getTier(ccc);
        const properties = [warningPropertyDescriptor, fileURLOutputPropertyHandler];
        if (getFeatureSetting("printScreenToPDFAdvanced", ccc.appID)) {
            properties.push(orientationPropertyHandler, zoomPercentagePropertyDescriptor);
        }

        return {
            name: this.name,
            group: ActionGroup.Communication,
            groupItemOrder: 9,
            properties,
            outputs,
            isLegacy: !isExperimentEnabled("printScreenToPDF", ccc.userFeatures),
            disabledReason: tier !== undefined ? ActionDisabledReason.RequiresUpgrade : undefined,
        };
    }

    public inflate(
        ib: WireActionInflationBackend,
        desc: PrintScreenToPDFActionDescription,
        arb: WireActionResultBuilder
    ): WireActionHydrator | WireActionResult {
        const {
            appFacilities,
            adc: {
                appID,
                appDescription: { theme },
            },
        } = ib;

        const fileURLOutputProperty = fileURLOutputPropertyHandler.getProperty(desc);
        const { tableAndColumn, setterMaker } = ib.getValueSetterForProperty(fileURLOutputProperty, "file-url");
        if (tableAndColumn === undefined && fileURLOutputProperty !== undefined)
            return arb.inflationError("Invalid column");

        const orientation = orientationPropertyHandler.getEnum(desc);
        const [zoomPercentageGetter] = inflateNumberProperty(ib, desc.zoomPercentage);

        return (vp, skipLoading) => {
            const setter = setterMaker(vp);
            if (isLoadingValue(setter)) return arb.errorIfSkipLoading(skipLoading, "Destination column");
            if (setter === undefined && fileURLOutputProperty !== undefined) {
                return arb.error(true, "Cannot write to column");
            }

            const maybeZoomPercentage = zoomPercentageGetter(vp);
            if (isLoadingValue(maybeZoomPercentage)) return arb.errorIfSkipLoading(skipLoading, "Zoom percentage");

            const zoomPercentage = maybeZoomPercentage ?? defaultZoomPercentage;
            if (
                !Number.isFinite(zoomPercentage) ||
                zoomPercentage < minZoomPercentage ||
                zoomPercentage > maxZoomPercentage
            ) {
                return arb.error(true, `Zoom percentage must be between ${minZoomPercentage} and ${maxZoomPercentage}`);
            }

            return async ab => {
                const wireScreen = ab.getCurrentWireScreen();
                if (!wireScreen.ok) return arb.fromResult(wireScreen);

                const body: PrintScreenToPDFRequestBody = {
                    appID,
                    wireScreen: wireScreen.result,
                    theme,
                    orientation,
                    zoomPercentage,
                };
                const response = await appFacilities.callAuthIfAvailableCloudFunction("printScreenToPDF", body, {});
                if (!isResponseOK(response)) {
                    const message = await getResponseErrorMessage(response);
                    return arb.errorFromHTTPStatus(response?.status ?? 500, message ?? "Unknown error");
                }

                const responseBody = await response.json();
                const fileURL = checkString(responseBody.temporaryFileURL);

                if (setter !== undefined) {
                    const writeResult = await setter(ab, fileURL);
                    if (!writeResult.ok) return arb.fromResult(writeResult);
                }

                return arb.fromResult(
                    Result.Ok({
                        [fileURLResultName]: fileURL,
                    })
                );
            };
        };
    }
}
